import type { Except, Optional } from '@festina-life/core'
import cx from 'classnames'
import { type ComponentProps, type RefObject, useMemo, useRef } from 'react'

import { mergeRefs } from '../../libs/refs'
import { ClearButton } from '../internal/clearButton/ClearButton'
import { clearButtonStyles } from '../internal/clearButton/clearButtonStyles'
import { clearNativeInputValue } from '../internal/clearButton/clearNativeInputValue'
import { rawTextInputStyles } from '../textInput/rawTextInputStyles'

export type RawTextAreaProps = Except<
  ComponentProps<'textarea'>,
  'rows' | 'cols' | 'readOnly' // we give a sensible default with autosizing, and only support `disabled`
> & {
  isInError?: boolean
  value?: string
  disabled?: boolean
  className?: Optional<string>
  isHidden?: boolean
  rows?: number
  /**
   * Whether the text area should automatically adjust its height based on the content.
   * Constrained to max 15 lines by default
   */
  isAutoSizing?: boolean
  ref?: RefObject<HTMLTextAreaElement>
}

/** Text area that can be used in Festina Forms to display a text area that follows our styling rules. May or may
 * not be wrapped by a FormInput component to add labels, descriptions and error messages around the input field.
 */
export const RawTextArea = ({
  isInError,
  disabled,
  value,
  className,
  isHidden,
  isAutoSizing = true,
  ref,
  rows = 4,
  onFocus,
  ...other
}: RawTextAreaProps) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const mergedRef = useMemo(() => mergeRefs(textAreaRef, ref), [ref])
  const valueToShow = value ?? (other.defaultValue ? undefined : '')
  const isClearable = !!valueToShow && !disabled
  const styles = cx(
    rawTextInputStyles({
      disabled: !!disabled,
      isInError: !!isInError,
      isAfterAddOn: false,
    }),
    'whitespace-pre-wrap', // ensure long text wraps into multiple lines instead of overflowing horizontally
    isAutoSizing ? 'resize-none col-start-1 row-start-1' : 'resize min-w-full',
    isClearable && clearButtonStyles.inputPadding,
    className,
  )
  return (
    <div
      className={cx(
        'relative w-full grid',
        'max-w-inputs-and-alerts', // ensure text areas don't get rediculously wide when they have a lot of content in one line
        clearButtonStyles.group,
      )}
    >
      <textarea
        className={cx(styles, 'overflow-auto')}
        data-autofocus
        rows={rows}
        aria-invalid={isInError}
        disabled={disabled}
        ref={mergedRef}
        value={valueToShow}
        onFocus={(event) => {
          event.target.select()
          onFocus?.(event)
        }}
        {...other}
      />
      {isAutoSizing && (
        <div
          className={cx(
            styles,
            'line-clamp-15 pointer-events-none min-h-0 invisible',
          )}
        >
          {valueToShow}
          {
            '\n\u0020' /* Extra content to add new empty lines visually before reaching the end */
          }
        </div>
      )}
      {isClearable && (
        <div
          className={cx(
            'absolute top-1 inset-e-1',
            'z-2', // since the focused text area is raised to z-1
            isInError && 'text-error-500',
          )}
        >
          <ClearButton
            onClear={() => {
              if (textAreaRef.current) {
                clearNativeInputValue(textAreaRef.current)
              }
            }}
          />
        </div>
      )}
    </div>
  )
}
